import { useContext, useEffect, useState } from "react";
import { AppContext } from "../App";
import { canManagePayments } from "../accessControl";
import { callApi, formatMoney, getInlineLoader } from "../Helpers";
import { MdInfoOutline, MdCalendarToday, MdCreditCard, MdHistory } from "react-icons/md";

export default function EditPayment({ uuid }) {
    const appContext = useContext(AppContext);
    const [ready, setReady] = useState(false);
    const [loading, setLoading] = useState(false);
    
    // Form Inputs
    const [newAmount, setNewAmount] = useState('');
    const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
    const [data, setData] = useState(null);

    useEffect(() => {
        if (!canManagePayments(appContext.currentUserData)) {
            appContext.tellError("Access denied.");
            return appContext.navTo({ item: "dashboard" });
        }
        if (uuid) getData();
    }, [uuid]);

    async function getData() {
        setReady(false);
        const res = await callApi('get-payment-data', { uuid });
        if (res.status === 1) {
            setData(res.data);
        } else {
            appContext.tellError('Could not fetch data');
        }
        setReady(true);
    }

    async function handleUpdate() {
        if (!newAmount || Number(newAmount) <= 0) return appContext.tellError("Enter a valid amount");
        if (!paymentDate) return appContext.tellError("Select a date");

        const originalPaidAmount = Number(data?.amount || 0);
        const currentRemainingBalance = Number(data?.balance || 0);
        const maximumAllowedAmount = currentRemainingBalance + originalPaidAmount;

        if (Number(newAmount) > maximumAllowedAmount) {
            return appContext.tellError(`Amount exceeds total outstanding limit of ${formatMoney(maximumAllowedAmount)}`);
        }

        setLoading(true);
        const res = await callApi('update-payment', { 
            uuid, 
            debtID: data?.debtID,          // Link directly back to loan records
            amount: originalPaidAmount,    // Send old amount to add back to debt balance
            newAmount: Number(newAmount),  // Send new amount for fresh updates
            paymentDate 
        });
        setLoading(false);

        if (res.status === 1) {
            appContext.tellMessage('Payment record successfully modified and debt updated');
            if (appContext.getAllDebts) appContext.getAllDebts();
            appContext.navBack();
        } else {
            appContext.tellError(res.msg || 'Failed to update payment record');
        }
    }

    useEffect(() => {
        if (data) {
            
            const rawDate = data.dateCreated || data.paymentDate;
            if (rawDate) {
                let parsedDate;
                if (typeof rawDate === "number" || /^\d{10}$/.test(String(rawDate).trim())) {
                    parsedDate = new Date(Number(rawDate) * 1000);
                } else {
                    parsedDate = new Date(rawDate);
                }

                if (!isNaN(parsedDate.getTime())) {
                    setPaymentDate(parsedDate.toISOString().split('T')[0]);
                } else {
                    setPaymentDate(new Date().toISOString().split('T')[0]);
                }
            } else {
                setPaymentDate(new Date().toISOString().split('T')[0]);
            }
        }
    }, [data]);

    if (!ready || !data) return <div className="container mSupportLoading">{getInlineLoader()}</div>;

    const originalPaidAmount = Number(data?.amount || 0);
    const currentRemainingBalance = Number(data?.balance || 0);
    const restoredLoanBalance = currentRemainingBalance + originalPaidAmount;

    return (
        <div className="container py-2 py-sm-4">
            <div className="app-form-page">
                <div className="app-form-shell bg-white rounded shadow-sm p-3 p-sm-4 border">
                    
                    {/* Header Banner */}
                    <div className="app-form-header border-bottom pb-3 mb-4">
                        <h5 className="app-form-title font-bold text-primary mb-1">Edit Payment</h5>
                        <p className="text-muted font-semi-bold">{data?.fullname} <br/>
                            <strong className="text-dark small">{data?.model}</strong>
                        </p>
                    </div>

                    {/* Summary Context Matrix Cards - Responsive Grid Layout */}
                    <div className="app-form-section mb-4">
                        <div className="row g-2 g-sm-3 justify-content-center">
                            <div className="col-6">
                                <div className="app-form-summary-card p-2.5 p-sm-3 border rounded bg-light h-100">
                                    <span className="text-muted d-block small text-uppercase tracking-wider mb-1" style={{ fontSize: '11px' }}>Balance</span>
                                    <strong className="text-dark d-block fs-6">{formatMoney(currentRemainingBalance)}</strong>
                                </div>
                            </div>
                            <div className="col-6 col-md-3">
                                <div className="app-form-summary-card p-2.5 p-sm-3 border border-warning rounded bg-white h-100">
                                    <span className="text-warning d-block small text-uppercase tracking-wider mb-1" style={{ fontSize: '11px' }}>RESET TO</span>
                                    <strong className="text-warning d-block fs-6">{formatMoney(restoredLoanBalance)}</strong>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Form Input Deck */}
                    <div className="app-form-section border rounded p-3 p-sm-4 bg-light-subtle">
                        <div className="row g-3">
                            
                            {/* Input 1: Processing Date */}
                            <div className="col-12 col-md-6 d-flex flex-column">
                                <label className="form-label small fw-bold text-muted mb-1 d-flex align-items-center gap-1">
                                    <MdCalendarToday size={14} /> Posting Date
                                </label>
                                <input 
                                    type="date" 
                                    className="form-control bg-white" 
                                    value={paymentDate} 
                                    max={new Date().toISOString().split('T')[0]} 
                                    onChange={(e) => setPaymentDate(e.target.value)} 
                                />
                            </div>

                            {/* Input 2: New Amount Tracker Controller */}
                            <div className="col-12 col-md-6 d-flex flex-column">
                                <label className="form-label small fw-bold text-muted mb-1 d-flex align-items-center gap-1">
                                    <MdCreditCard size={14} /> New Payment Amount (TZS)
                                </label>
                                <input 
                                    type="number" 
                                    className="form-control fw-bold text-primary bg-white" 
                                    value={newAmount} 
                                    onChange={(e) => setNewAmount(e.target.value)} 
                                />
                                <small className="text-muted small d-block mt-1 d-flex align-items-center gap-1">
                                    <MdHistory size={12} /> Original value paid: {formatMoney(originalPaidAmount)}
                                </small>
                            </div>
                        </div>

                        {/* Action Control Button Wrapper Row */}
                        <div className="d-flex flex-wrap align-items-center gap-2 mt-4 pt-3 border-top justify-content-end app-form-submit-row">
                            <button
                                type="button"
                                className="btn btn-danger px-4 flex-fill flex-sm-grow-0 order-2 order-sm-1"
                                onClick={() => appContext.navBack()}
                                disabled={loading}
                            >
                                Cancel
                            </button>
                            <button 
                                type="button" 
                                className="btn btn-success px-4 flex-fill flex-sm-grow-0 order-1 order-sm-2" 
                                onClick={handleUpdate} 
                                disabled={loading || !newAmount}
                            >
                                {loading ? "Saving Changes..." : "Save"}
                            </button>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
}